import RAPIER from '@dimforge/rapier3d-compat';
import type { Vec } from '../destruction/structure';

const ROTATION = { x: 0, y: 0, z: 0, w: 1 };
const OFFSET = 0.8415;

/** A negative volume query is shared only inside one synchronous walking pass.
 * Bodies must not move or be added during that pass. Occupied volumes retain
 * the original capsule cast, including sleeping rubble and kinematic vehicles.
 * No result survives into the next controller/physics update. */
export class DynamicFootQueries {
  private active = false;
  private empty = new Map<number, boolean>();
  private fineEmpty = new Map<number, boolean>();
  private readonly volume = new RAPIER.Cuboid(8.741, 3.3395, 8.741);
  private readonly fineVolume = new RAPIER.Cuboid(1.741, 3.3395, 1.741);
  private readonly center = { x: 0, y: 0, z: 0 };
  queries = 0;
  reused = 0;
  skipped = 0;
  casts = 0;
  fineQueries = 0;
  fineSkipped = 0;

  constructor(private readonly world: () => RAPIER.World) {}
  begin(): void {
    this.empty.clear();
    this.fineEmpty.clear();
    this.active = true;
  }
  end(): void {
    this.active = false;
    this.empty.clear();
    this.fineEmpty.clear();
  }
  reset(): void {
    this.end();
    this.queries = this.reused = this.skipped = this.casts = 0;
    this.fineQueries = this.fineSkipped = 0;
  }
  sweep(point: Vec, move: Vec, probe: RAPIER.Capsule): number | undefined {
    if (this.active && Math.max(Math.abs(move.x), Math.abs(move.y), Math.abs(move.z)) <= 0.5) {
      const x = Math.floor(point.x / 16),
        y = Math.floor(point.y / 4),
        z = Math.floor(point.z / 16);
      // A collision-free numeric key; larger worlds simply use exact casts.
      if (x >= -512 && x < 512 && y >= -512 && y < 512 && z >= -512 && z < 512) {
        const key = ((x + 512) * 1024 + y + 512) * 1024 + z + 512;
        let empty = this.empty.get(key);
        if (empty === undefined) {
          this.center.x = (x + 0.5) * 16;
          this.center.y = (y + 0.5) * 4 + OFFSET;
          this.center.z = (z + 0.5) * 16;
          // Contains every capsule and <=0.5m sweep originating in this cell,
          // with a margin beyond the exact cast's target distance.
          empty = !this.world().intersectionWithShape(
            this.center,
            ROTATION,
            this.volume,
            RAPIER.QueryFilterFlags.EXCLUDE_FIXED,
          );
          this.empty.set(key, empty);
          this.queries++;
        } else this.reused++;
        if (empty) {
          this.skipped++;
          return 1;
        }
        // A car anywhere in a 16m tile used to force exact casts for every
        // pavement walker in that tile. Refine occupied tiles to a conservative
        // 2m volume before falling back; the full capsule/sweep still fits.
        const fx = Math.floor(point.x / 2),
          fz = Math.floor(point.z / 2);
        const fineKey = ((fx + 4096) * 1024 + y + 512) * 8192 + fz + 4096;
        let fine = this.fineEmpty.get(fineKey);
        if (fine === undefined) {
          this.center.x = (fx + 0.5) * 2;
          this.center.z = (fz + 0.5) * 2;
          this.center.y = (y + 0.5) * 4 + OFFSET;
          fine = !this.world().intersectionWithShape(
            this.center,
            ROTATION,
            this.fineVolume,
            RAPIER.QueryFilterFlags.EXCLUDE_FIXED,
          );
          this.fineEmpty.set(fineKey, fine);
          this.fineQueries++;
        }
        if (fine) {
          this.fineSkipped++;
          this.skipped++;
          return 1;
        }
      }
    }
    this.casts++;
    return this.world().castShape(
      { x: point.x, y: point.y + OFFSET, z: point.z },
      ROTATION,
      move,
      probe,
      0.0001,
      1,
      false,
      RAPIER.QueryFilterFlags.EXCLUDE_FIXED,
    )
      ? undefined
      : 1;
  }
}
